import { LayoutDashboard, BookOpen, GraduationCap, Users, BarChart3, CalendarDays, Briefcase, Wallet, UserCog, Library, ShieldCheck, GraduationCap as StudentIcon, Bot, Upload, Mail, ShieldAlert, Settings, Wrench } from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'

import { useDashboardStore } from '@/core/store/dashboardStore'
import { useAuthStore } from '@/core/store/authStore'
import { useInboxStore } from '@/features/emails/stores/inboxStore'
import { useAgentStore } from '@/features/agents/stores/agentStore'
import { cn } from '@/core/lib/utils'
import type { UserRole } from '@/core/store/authStore'

const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  instructor: 'Instructor',
  staff: 'Staff',
  company_admin: 'Company Admin',
  student: 'Student',
}

const ROLE_ICONS: Record<UserRole, React.ElementType> = {
  admin: ShieldCheck,
  instructor: GraduationCap,
  staff: Users,
  company_admin: Briefcase,
  student: StudentIcon,
}

const ROLE_COLORS: Record<UserRole, string> = {
  admin: 'text-red-400',
  instructor: 'text-blue-400',
  staff: 'text-green-400',
  company_admin: 'text-purple-400',
  student: 'text-yellow-400',
}

/* ------------------------------------------------------------------ */
/*  Navigation items by role                                            */
/* ------------------------------------------------------------------ */

/* ─── Operational Section ─── */
const operationalItems = [
  { to: '/overview', label: 'Dashboard', icon: LayoutDashboard, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
  { to: '/classes', label: 'Courses & Classes', icon: GraduationCap, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
  // LEGACY (2026-05-31): /calendar is a read-only monthly view kept for reference.
  // The unified calendar is now at /admin/schedules.
  // { to: '/calendar', label: 'Calendar', icon: CalendarDays, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
  { to: '/courses', label: 'Online Courses', icon: BookOpen, roles: ['admin', 'instructor', 'staff', 'company_admin', 'student'] as UserRole[] },
  { to: '/crm', label: 'CRM', icon: Users, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
  { to: '/emails', label: 'Emails', icon: Mail, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
  { to: '/reports', label: 'Reports', icon: BarChart3, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
  { to: '/ai', label: 'AI Hub', icon: Bot, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
  { to: '/settings', label: 'Settings', icon: Settings, roles: ['admin', 'instructor', 'staff', 'company_admin'] as UserRole[] },
]

/* ─── Administration Section ─── */
const adminItems = [
  { to: '/admin/users', label: 'Users', icon: UserCog },
  { to: '/admin/students', label: 'Students', icon: StudentIcon },
  { to: '/admin/companies', label: 'Companies', icon: Briefcase },
  { to: '/admin/finances', label: 'Finances', icon: Wallet },
  { to: '/admin/courses', label: 'Courses', icon: Library },
  { to: '/admin/schedules', label: 'Schedules', icon: CalendarDays },
  { to: '/admin/import', label: 'Import Data', icon: Upload },
  { to: '/admin/settings', label: 'Admin Settings', icon: Wrench },
]

export function Sidebar() {
  const sidebarOpen = useDashboardStore((s) => s.sidebarOpen)
  const toggleSidebar = useDashboardStore((s) => s.toggleSidebar)
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const user = useAuthStore((s) => s.user)
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const role = (user?.role ?? 'staff') as UserRole

  const RoleIcon = ROLE_ICONS[role]
  const roleLabel = ROLE_LABELS[role]
  const roleColor = ROLE_COLORS[role]

  // Filter nav items by current user role
  const navItems = operationalItems.filter((item) => item.roles.includes(role))
  const emailUnreadCount = useInboxStore((s) => s.unreadCount)
  const agentPendingCount = useAgentStore((s) => s.pendingCount)

  return (
    <>
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={toggleSidebar}
        />
      )}

      <aside
        className={cn(
          'fixed top-0 left-0 h-full z-30 bg-navy text-white transition-all duration-300 flex flex-col',
          sidebarOpen ? 'w-64' : 'w-0 overflow-hidden lg:w-16'
        )}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 h-16 border-b border-navy-light shrink-0">
          <img
            src="/images/logo/ub-logo-128.webp"
            alt="UBSafety"
            className="w-8 h-8 shrink-0 rounded-sm object-contain"
          />
          {sidebarOpen && (
            <span className="font-bold text-white text-lg whitespace-nowrap">UBSafety</span>
          )}
        </div>

        {/* Nav */}
        <nav className="mt-4 flex flex-col gap-1 px-2 flex-1 overflow-y-auto min-h-0">
          {navItems.map(({ to, label, icon: Icon }) => {
            const isActive = pathname === to
            return (
              <Link
                key={to}
                to={to}
                onClick={() => {
                  if (window.innerWidth < 1024) {toggleSidebar()}
                }}
                title={!sidebarOpen ? label : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full text-left',
                  isActive
                    ? 'bg-primary text-white'
                    : 'text-slate-400 hover:bg-navy-light hover:text-white'
                )}
              >
                <Icon className="w-5 h-5 shrink-0" />
                {sidebarOpen && <span className="whitespace-nowrap">{label}</span>}
                {to === '/emails' && emailUnreadCount > 0 && (
                  <span className={cn(
                    'ml-auto text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center',
                    isActive ? 'bg-white text-primary' : 'bg-primary text-white'
                  )}>
                    {emailUnreadCount > 99 ? '99+' : emailUnreadCount}
                  </span>
                )}
              </Link>
            )
          })}

          {/* Admin section — only visible to admin users */}
          {/* AI Agents section */}
          {isAdmin && (
            <>
              {sidebarOpen && (
                <div className="mt-6 mb-2 px-3 text-xs font-semibold text-sidebar-muted-secondary uppercase tracking-wider">
                  AI Agents
                </div>
              )}
              <Link
                to="/approvals"
                onClick={() => { if (window.innerWidth < 1024) {toggleSidebar()} }}
                title={!sidebarOpen ? 'Approvals' : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full text-left',
                  pathname === '/approvals'
                    ? 'bg-primary text-white'
                    : 'text-slate-400 hover:bg-navy-light hover:text-white'
                )}
              >
                <ShieldAlert className="w-5 h-5 shrink-0" />
                {sidebarOpen && <span className="whitespace-nowrap">Approvals</span>}
                {agentPendingCount > 0 && (
                  <span className={cn(
                    'ml-auto text-xs font-bold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center',
                    pathname === '/approvals' ? 'bg-white text-primary' : 'bg-primary text-white'
                  )}>
                    {agentPendingCount > 99 ? '99+' : agentPendingCount}
                  </span>
                )}
              </Link>
              <Link
                to="/agents/settings"
                onClick={() => { if (window.innerWidth < 1024) {toggleSidebar()} }}
                title={!sidebarOpen ? 'Agent Settings' : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full text-left',
                  pathname === '/agents/settings'
                    ? 'bg-primary text-white'
                    : 'text-slate-400 hover:bg-navy-light hover:text-white'
                )}
              >
                <Settings className="w-5 h-5 shrink-0" />
                {sidebarOpen && <span className="whitespace-nowrap">Agent Settings</span>}
              </Link>
            </>
          )}

          {/* Admin section */}
          {isAdmin && (
            <>
              {sidebarOpen && (
                <div className="mt-6 mb-2 px-3 text-xs font-semibold text-sidebar-muted-secondary uppercase tracking-wider">
                  Administration
                </div>
              )}
              {adminItems.map(({ to, label, icon: Icon }) => {
                const isActive = pathname.startsWith(to)
                return (
                  <Link
                    key={to}
                    to={to}
                    onClick={() => {
                      if (window.innerWidth < 1024) {toggleSidebar()}
                    }}
                    title={!sidebarOpen ? label : undefined}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors w-full text-left',
                      isActive
                        ? 'bg-primary text-white'
                        : 'text-slate-400 hover:bg-navy-light hover:text-white'
                    )}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    {sidebarOpen && <span className="whitespace-nowrap">{label}</span>}
                  </Link>
                )
              })}
            </>
          )}
        </nav>

        {/* Role badge at bottom */}
        <div className="shrink-0 border-t border-navy-light px-3 py-3">
          <div
            className={cn(
              'flex items-center gap-2 text-xs font-medium',
              sidebarOpen ? 'px-2 py-1.5 rounded-md bg-navy-light/50' : 'justify-center'
            )}
            title={roleLabel}
          >
            <RoleIcon className={cn('w-4 h-4 shrink-0', roleColor)} />
            {sidebarOpen && (
              <span className={cn('whitespace-nowrap', roleColor)}>
                {roleLabel}
              </span>
            )}
            {sidebarOpen && (
              <span className="ml-auto text-sidebar-muted-secondary">
                v{import.meta.env.APP_VERSION}
              </span>
            )}
          </div>
        </div>
      </aside>
    </>
  )
}
